<script>
/* eslint-env browser */
/* global FB */
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ChannelApi from '../../../../../api/channels';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
    NextButton,
    ComboBox,
    // eslint-disable-next-line vue/no-reserved-component-names
    Button,
    // eslint-disable-next-line vue/no-unused-components
    Icon,
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
      activeTab: 'sdk',
      isRequestingAuthorization: false,
      manualForm: {
        pageId: '',
        inboxName: '',
        pageAccessToken: '',
        userAccessToken: '',
      },
      isCreatingManual: false,
    };
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    comboBoxPageOptions() {
      return this.getSelectablePages.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async startLogin() {
      this.hasLoginStarted = true;
      this.isRequestingAuthorization = true;
      try {
        // this will load the SDK in a promise, and resolve it when the sdk is loaded
        // in case the SDK is already present, it will resolve immediately
        await this.loadFBsdk();
        this.runFBInit(); // run init anyway, `tryFBlogin` won't wait for `fbAsyncInit` otherwise.
        this.tryFBlogin(); // make an attempt to login
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          // if the error was related to script loading, we show a toast
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          // if the error was anything else, we capture it and show a toast
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      } finally {
        this.isRequestingAuthorization = false;
      }
    },

    setPageName(pageId) {
      const page = this.pageList.find(p => p.id === pageId);
      if (page) {
        this.selectedPage = page;
        this.pageName = page.name;
      } else {
        this.selectedPage = { name: null, id: null };
        this.pageName = '';
      }
    },

    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK COMMENTS AUTH ERROR', response);
            this.hasError = true;
            this.hasLoginStarted = false;
            // The person is logged into Facebook, but not your app.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK COMMENTS AUTH ERROR', response);
            this.hasError = true;
            this.hasLoginStarted = false;
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          scope:
            'pages_manage_metadata,pages_manage_posts,pages_read_engagement,pages_manage_engagement,pages_read_user_content',
        }
      );
    },

    async fetchPages(token) {
      try {
        // Usar ChannelApi como lo hace Facebook Pages - axios maneja auth automáticamente
        const response = await ChannelApi.fetchFacebookCommentsPages(
          token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pages:', error);
        this.hasError = true;
        this.hasLoginStarted = false;
        this.errorStateMessage = this.$t(
          'INBOX_MGMT.ADD.FACEBOOK_COMMENTS.ERROR_MESSAGE'
        );
        this.errorStateDescription =
          error.message ||
          this.$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.UNEXPECTED_ERROR');
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name?.trim(),
      };
    },

    createChannel() {
      if (!this.selectedPage.id) return;

      this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
      this.isCreating = true;

      // Usar el endpoint específico de Facebook Comments
      ChannelApi.createFacebookCommentsChannel(
        this.channelParams(),
        this.accountId
      )
        .then(response => {
          const { data } = response;
          router.replace({
            name: 'settings_inboxes_add_agents',
            params: { page: 'new', inbox_id: data.inbox_id },
          });
        })
        .catch(error => {
          this.isCreating = false;
          this.hasError = true;
          this.errorStateMessage = this.$t(
            'INBOX_MGMT.ADD.FACEBOOK_COMMENTS.ERROR_CONNECTING'
          );
          this.errorStateDescription =
            error.response?.data?.errors?.join(', ') || error.message;
        });
    },

    async connectManualPage() {
      const { pageId, pageAccessToken, userAccessToken, inboxName } =
        this.manualForm;
      if (!pageId || !pageAccessToken || !inboxName) {
        useAlert(this.$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.FILL_ALL_FIELDS'));
        return;
      }
      this.isCreatingManual = true;
      try {
        const response = await ChannelApi.createFacebookCommentsChannel(
          {
            page_id: pageId,
            page_access_token: pageAccessToken,
            user_access_token: userAccessToken,
            inbox_name: inboxName.trim(),
          },
          this.accountId
        );
        const { data } = response;
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.inbox_id },
        });
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.ERROR_CONNECTING'));
        // eslint-disable-next-line no-console
        console.error('Error creating channel:', error);
      } finally {
        this.isCreatingManual = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <!-- Error State -->
      <div
        v-if="hasError"
        class="max-w-lg mx-auto text-center p-6 bg-n-ruby-1 rounded-lg"
      >
        <h5 class="text-n-ruby-12 font-medium mb-2">{{ errorStateMessage }}</h5>
        <p v-if="errorStateDescription" class="text-n-ruby-11 text-sm">
          {{ errorStateDescription }}
        </p>
        <Button
          class="mt-4"
          sm
          variant="secondary"
          :label="$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.TRY_AGAIN')"
          @click="hasError = false"
        />
      </div>

      <!-- SDK Flow - Loading/Pages Selection -->
      <div v-else-if="hasLoginStarted" class="w-full max-w-lg">
        <div v-if="hasError" class="max-w-lg mx-auto text-center">
          <h5 class="text-n-ruby-12 font-medium mb-2">
            {{ errorStateMessage }}
          </h5>
          <p
            v-if="errorStateDescription"
            class="text-n-ruby-11 text-sm"
            v-html="errorStateDescription"
          />
          <Button
            class="mt-4"
            sm
            variant="secondary"
            :label="$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.TRY_AGAIN')"
            @click="
              hasError = false;
              hasLoginStarted = false;
            "
          />
        </div>
        <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
        <form
          v-else
          class="flex flex-col flex-wrap mx-0"
          @submit.prevent="createChannel()"
        >
          <div class="w-full">
            <PageHeader
              :header-title="$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.SELECT_PAGE')"
              :header-content="
                $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.SELECT_PAGE_HELP')
              "
            />
          </div>
          <div class="w-full mt-4">
            <div class="w-full mb-2">
              <div class="input-wrap" :class="{ error: !selectedPage.name }">
                <span class="text-n-slate-12 text-start">
                  {{ $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.SELECT_PAGE') }}
                </span>
                <ComboBox
                  :model-value="selectedPage.id"
                  :options="comboBoxPageOptions"
                  :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
                  :has-error="!selectedPage.name"
                  class="[&>div>button]:!bg-n-alpha-black2 mt-1"
                  @update:model-value="setPageName"
                />
              </div>
            </div>
            <div class="w-full text-right mt-4">
              <NextButton
                :label="$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.CONNECT_PAGE')"
                :is-loading="isCreating"
                type="submit"
              />
            </div>
          </div>
        </form>
      </div>

      <!-- Connection Options -->
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak max-w-lg w-full"
      >
        <h6 class="text-2xl font-medium">
          {{ $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.TITLE') }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11 max-w-md">
          {{ $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.DESCRIPTION') }}
        </p>

        <!-- Tabs -->
        <div class="flex gap-2 mb-6 w-full">
          <button
            type="button"
            class="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            :class="
              !isManualMode
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
            "
            @click="isManualMode = false"
          >
            {{ $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.TAB_OAUTH') }}
          </button>
          <button
            type="button"
            class="flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            :class="
              isManualMode
                ? 'bg-n-brand text-white'
                : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
            "
            @click="isManualMode = true"
          >
            {{ $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.TAB_MANUAL') }}
          </button>
        </div>

        <!-- SDK Mode -->
        <div v-if="!isManualMode" class="w-full space-y-4">
          <p class="text-sm text-n-slate-11 mb-4">
            {{
              replaceInstallationName(
                $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.OAUTH_DESCRIPTION')
              )
            }}
          </p>
          <a href="#" @click.prevent="startLogin()">
            <img
              class="w-auto h-10 rounded-md mx-auto"
              src="~dashboard/assets/images/channels/facebook_login.png"
              alt="Facebook-logo"
            />
          </a>
        </div>

        <!-- Manual Mode -->
        <div v-else class="w-full space-y-4 mb-6 text-left">
          <p class="text-sm text-n-slate-11 mb-4 text-center">
            {{ $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.MANUAL_DESCRIPTION') }}
          </p>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">{{
              $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.MANUAL_PAGE_ID_LABEL')
            }}</label>
            <input
              v-model="manualForm.pageId"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.FACEBOOK_COMMENTS.MANUAL_PAGE_ID_PLACEHOLDER'
                )
              "
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">{{
              $t('INBOX_MGMT.ADD.FB.INBOX_NAME')
            }}</label>
            <input
              v-model="manualForm.inboxName"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">{{
              $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.MANUAL_PAGE_TOKEN_LABEL')
            }}</label>
            <input
              v-model="manualForm.pageAccessToken"
              type="password"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.FACEBOOK_COMMENTS.MANUAL_PAGE_TOKEN_PLACEHOLDER'
                )
              "
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">{{
              $t('INBOX_MGMT.ADD.FB.MANUAL.USER_ACCESS_TOKEN')
            }}</label>
            <input
              v-model="manualForm.userAccessToken"
              type="password"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('INBOX_MGMT.ADD.FB.MANUAL.USER_ACCESS_TOKEN_PLACEHOLDER')
              "
            />
          </div>
          <Button
            class="text-white !rounded-full !px-6 bg-[#1877F2] w-full mt-4"
            lg
            icon="i-lucide-link"
            :disabled="
              isCreatingManual ||
              !manualForm.pageId ||
              !manualForm.pageAccessToken ||
              !manualForm.inboxName
            "
            :is-loading="isCreatingManual"
            :label="$t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.CONNECT_MANUALLY')"
            @click="connectManualPage()"
          />
        </div>

        <p class="text-xs text-n-slate-9 mt-4">
          {{
            replaceInstallationName(
              $t('INBOX_MGMT.ADD.FACEBOOK_COMMENTS.PERMISSIONS_NOTE')
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>
